import type { User } from "@types";
import { allMobileNavItems } from "@/components/MainNavigation/headerNavItems";
import { canReachPath } from "./navAccess";
import { permissionsForUser } from "./permissions";

/**
 * Reps land in Field Sales with zero setup — the module is their whole job and
 * `/field-sales` is guaranteed reachable via the rep route allowlist.
 */
const REP_FALLBACK_MODULE = "/field-sales";
const DISPATCHER_FALLBACK_MODULE = "/dispatch";
const TECHNICIAN_FALLBACK_MODULE = "/dispatch/my-day";
const RESTRICTED_SAFE_FALLBACK_MODULE = "/user-settings";

const isDispatchModule = (path: string): boolean =>
  path === "/dispatch" || path.startsWith("/dispatch/");

/**
 * Select-item value meaning "use the standard role-based landing" (persisted
 * as `defaultModule: null`). Radix Select items can't carry an empty string,
 * hence a sentinel. Shared by the Team drawer and UserSettings so the
 * initialize/compare pair can never drift between the two forms.
 */
export const STANDARD_LANDING_VALUE = "__standard__";

/** The Home page's old address; it now redirects to the Operator chat. */
const LEGACY_HOME_MODULE = "/home";
const HOME_MODULE = "/operator/chat";

/**
 * The picker value for a stored landing preference: the standard sentinel when
 * none is stored, and the Home entry for a preference saved while Home lived
 * at `/home`, so the picker still shows it and saving does not write it back.
 */
export const landingFormValue = (stored?: string | null): string => {
  if (!stored) return STANDARD_LANDING_VALUE;
  return stored === LEGACY_HOME_MODULE ? HOME_MODULE : stored;
};

/**
 * Resolve the user's preferred landing module (SD-10992).
 *
 * Returns `user.defaultModule` iff it is set AND the user's role can actually
 * reach it (same `canReachPath` authority as the nav projection and route
 * guard) — so a stale preference after a role change degrades safely to the
 * standard landing instead of bouncing off the guard. With nothing (valid)
 * set, reps get `/field-sales`; everyone else gets `null` = existing behavior.
 *
 * Pure + framework-free for unit testing. Deep links always win over this —
 * callers apply it only when there is no explicit destination
 * (see `getPostLoginPath`).
 */
export const resolveDefaultModule = (
  user?: User | null,
  dispatchEnabled = false,
): string | null => {
  if (!user) return null;
  const permissions = permissionsForUser(user);
  const stored = user.defaultModule;
  if (
    stored &&
    canReachPath(stored, user.role, permissions) &&
    (dispatchEnabled || !isDispatchModule(stored))
  ) {
    return stored;
  }
  if (user.role === "rep") return REP_FALLBACK_MODULE;
  if (user.role === "dispatcher")
    return dispatchEnabled
      ? DISPATCHER_FALLBACK_MODULE
      : RESTRICTED_SAFE_FALLBACK_MODULE;
  if (user.role === "technician")
    return dispatchEnabled
      ? TECHNICIAN_FALLBACK_MODULE
      : RESTRICTED_SAFE_FALLBACK_MODULE;
  return null;
};

export interface DefaultModuleOption {
  label: string;
  path: string;
}

/**
 * The landing-module choices offered for a TARGET user (the Team drawer edits
 * other users; UserSettings edits self): the header nav items that user's
 * role/permissions can reach. Derived from the same nav source + gate the app
 * renders with, so the picker can never offer an unreachable page.
 */
export const defaultModuleOptions = (
  user?: User | null,
): DefaultModuleOption[] => {
  if (!user) return [];
  const permissions = permissionsForUser(user);
  const fromNav = allMobileNavItems
    .filter((item) => canReachPath(item.href, user.role, permissions))
    .map((item) => ({ label: item.label, path: item.href }));
  // The nav carries `/dispatch`, which technicians cannot reach — their board
  // is `/dispatch/my-day`, which TopNavBar substitutes at render time and so
  // never appears as its own nav item. Without this the picker offers a
  // technician no dispatch landing at all, even though it is where the role
  // lands by default. Callers still run the result through
  // `filterDispatchDefaultModules`, so it disappears when the flag is off.
  if (
    user.role === "technician" &&
    canReachPath(TECHNICIAN_FALLBACK_MODULE, user.role, permissions) &&
    !fromNav.some((option) => option.path === TECHNICIAN_FALLBACK_MODULE)
  ) {
    fromNav.push({ label: "My day", path: TECHNICIAN_FALLBACK_MODULE });
  }
  return fromNav;
};
